'use client'

import { useCallback, useMemo, useRef } from 'react'
import isEmpty from 'lodash/isEmpty'
import { type AnalyticsMappingContext } from '@shipt/cdui-core/modules/analytics'
import {
  type ProductActionPayload,
  type ProductCommerceData,
} from '@shipt/cdui-modules/product-shelf/schema'
import { useProductCommerceGate } from '@/features/shared/hooks/useProductCommerceGate'
import { useBasket } from '@/features/shared/hooks/useBasket'
import { useStoreParams } from '@/features/shared/utils/dataFetching/storeParams'
import { type GlobalStoreParams } from '@shared/types'
import { useUpdateProductQuantity } from '@/features/shared/hooks/useUpdateProductQuantity'
import { DISPLAY_FORMAT } from '@/features/shared/constants/analytics'
import {
  trackProductClicked,
  trackProductViewed,
  useTrackProduct,
} from '@/features/shared/analytics/products'
import { useHandleDialog } from '@/features/shared/state/Dialog/useHandleDialog'
import { DynamicVariationsQuickAddModal } from '@/features/shop/components/Modal/VariationsQuickAddModal/DynamicVariationsQuickAddModal'
import { DynamicProductInstructionsModal } from '@/features/shop/components/Modal/ProductInstructionsModal/DynamicProductInstructionsModal'
import { DynamicOutOfStockModal } from '@/features/shop/components/Modal/OutOfStockModal/DynamicOutOfStockModal'
import { getQtyIncrementForProduct } from '@/features/shop/components/ProductCard/Utils'
import { useFetchSubstitutionsQuery } from '@/features/shop/services/Substitutions/hooks'
import { SubstitutionForProductState } from '@/features/shop/services/Substitutions/types'
import { getIsProductPreference } from '@/features/shop/services/Substitutions/utils'
import {
  useActiveOrder,
  useCurrentActiveOrderId,
} from '@/features/shop/state/SelectedActiveOrder/hooks'
import { useMutationUpdateCart } from '@/features/shop/services/Cart/mutations'
import {
  useMutationAddToCurrentOrder,
  useMutationUpdateOrder,
} from '@/features/shop/services/Orders/mutations'
import {
  toAnalyticsProduct,
  toCommerceGateProduct,
  toInstructionsModalProduct,
  toMutationProduct,
  toSubstituteProduct,
  toVariationModalProduct,
} from '@/features/cdui/productCommerce'

const quantityDeltaFor = (commerce: ProductCommerceData, payload: ProductActionPayload) =>
  payload.quantityDelta ??
  commerce.quantityIncrement ??
  getQtyIncrementForProduct({
    type: commerce.productType ?? '',
    hasCustomLabel: commerce.hasCustomLabel ?? false,
    unitWeight: commerce.unitWeight ?? 0,
    bogo: commerce.bogo ?? false,
  })

const productEventProps = (
  commerce: ProductCommerceData,
  payload: ProductActionPayload,
  analyticsContext?: AnalyticsMappingContext,
  storeParams?: GlobalStoreParams,
) => ({
  product: toAnalyticsProduct(commerce),
  featured_transaction_id: commerce.featuredTransactionId,
  // urchin's ads placement for sponsored products and the legacy shelf sends it only when set
  ...(commerce.adPlacementId && { ad_placement_id: commerce.adPlacementId }),
  displayFormat: DISPLAY_FORMAT.HORIZONTAL,
  shelfIndex: analyticsContext?.shelfIndex,
  subLocation: analyticsContext?.placement.display_sublocation,
  listIndex: payload.productIndex,
  // the legacy shelf (useOnShelfProduct) sends store ids too, so we need them to compare the two arms
  ...(storeParams && { storeParams }),
})

export const useCduiProductCommerce = () => {
  const runCommerceGate = useProductCommerceGate()
  const { getProductQuantity, getProductNote } = useBasket()
  const { updateProductQuantity } = useUpdateProductQuantity()
  const { trackProductAdded, trackProductRemoved } = useTrackProduct()
  const { openDialog } = useHandleDialog()
  const fetchSubstitutions = useFetchSubstitutionsQuery()
  const currentActiveOrderId = useCurrentActiveOrderId()
  const currentOrder = useActiveOrder()
  const { mutateAsync: updateCart } = useMutationUpdateCart()
  const { mutateAsync: updateOrder } = useMutationUpdateOrder()
  const { mutateAsync: addToCurrentOrder } = useMutationAddToCurrentOrder()
  const { store_id, metro_id, store_location_id } = useStoreParams()
  // only send these when all three exist, same as useOnShelfProduct
  const storeParams = useMemo<GlobalStoreParams | undefined>(
    () =>
      store_id && metro_id && store_location_id
        ? { store_id, metro_id, store_location_id }
        : undefined,
    [store_id, metro_id, store_location_id],
  )

  // useActiveOrder resolves the selected id through a query, and this route is only
  // ever reached by a hard navigation, so there is always a window where an order is
  // selected but has not arrived yet. Every branch below reads a missing order as
  // "no order" and writes to the cart, so hold the mutation until the selection
  // resolves rather than let it silently pick the wrong basket.
  const isActiveOrderUnresolved = Boolean(currentActiveOrderId) && !currentOrder

  // Order mutations carry no optimistic cache update, so a second tap would re read the
  // pre mutation quantity and overwrite the first. Cart mutations are optimistic, so cart
  // taps are not held.
  const shouldHoldInFlight = Boolean(currentActiveOrderId)
  const productsInFlight = useRef(new Set<string>())

  const currentQuantity = useCallback(
    (commerce: ProductCommerceData) => getProductQuantity({ id: Number(commerce.id) }),
    [getProductQuantity],
  )

  const checkLowStock = useCallback(
    async (commerce: ProductCommerceData) => {
      if (!commerce.lowStock) return

      const {
        substitutions,
        preferredSubstitution: preference,
        state,
      } = await fetchSubstitutions({ productId: Number(commerce.id) })

      const unavailablePreference =
        (state !== SubstitutionForProductState.available && isEmpty(preference)) ||
        (getIsProductPreference(preference) && !preference.available)

      const shouldPrompt = isEmpty(substitutions) || state === SubstitutionForProductState.lowStock

      if (unavailablePreference && shouldPrompt) {
        openDialog(DynamicOutOfStockModal, {
          product: toSubstituteProduct(commerce),
        })
      }
    },
    [fetchSubstitutions, openDialog],
  )

  const addProductWhenReady = useCallback(
    async (payload: ProductActionPayload, analyticsContext?: AnalyticsMappingContext) => {
      const { product: commerce } = payload

      if (commerce.variationGroupId) {
        openDialog(DynamicVariationsQuickAddModal, {
          product: toVariationModalProduct(commerce),
          listIndex: payload.productIndex ?? analyticsContext?.shelfIndex,
        })
        return
      }

      if (shouldHoldInFlight) {
        if (productsInFlight.current.has(commerce.id)) return
        productsInFlight.current.add(commerce.id)
      }

      const quantityDelta = quantityDeltaFor(commerce, payload)
      const inCart = currentQuantity(commerce)
      const lowStockCheck = checkLowStock(commerce).catch(() => undefined)

      try {
        await updateProductQuantity({
          product: toMutationProduct(commerce),
          quantity: inCart + quantityDelta,
          itemUpdated: inCart > 0,
        })

        await lowStockCheck

        trackProductAdded({
          ...productEventProps(commerce, payload, analyticsContext, storeParams),
          quantity_added: quantityDelta,
        })
      } finally {
        productsInFlight.current.delete(commerce.id)
      }
    },
    [
      checkLowStock,
      currentQuantity,
      openDialog,
      shouldHoldInFlight,
      storeParams,
      trackProductAdded,
      updateProductQuantity,
    ],
  )

  const addProduct = useCallback(
    (payload: ProductActionPayload, analyticsContext?: AnalyticsMappingContext) => {
      if (isActiveOrderUnresolved) return

      runCommerceGate({
        product: toCommerceGateProduct(payload.product),
        onReady: () => {
          addProductWhenReady(payload, analyticsContext)
        },
      })
    },
    [addProductWhenReady, isActiveOrderUnresolved, runCommerceGate],
  )

  const removeProduct = useCallback(
    async (payload: ProductActionPayload, analyticsContext?: AnalyticsMappingContext) => {
      const { product: commerce } = payload

      if (isActiveOrderUnresolved) return
      if (shouldHoldInFlight) {
        if (productsInFlight.current.has(commerce.id)) return
        productsInFlight.current.add(commerce.id)
      }

      try {
        const quantityDelta = quantityDeltaFor(commerce, payload)

        await updateProductQuantity({
          product: toMutationProduct(commerce),
          quantity: Math.max(0, currentQuantity(commerce) - quantityDelta),
          itemUpdated: true,
        })

        trackProductRemoved({
          ...productEventProps(commerce, payload, analyticsContext, storeParams),
          quantity_removed: quantityDelta,
        })
      } finally {
        productsInFlight.current.delete(commerce.id)
      }
    },
    [
      currentQuantity,
      isActiveOrderUnresolved,
      shouldHoldInFlight,
      storeParams,
      trackProductRemoved,
      updateProductQuantity,
    ],
  )

  const viewProduct = useCallback(
    (payload: ProductActionPayload, analyticsContext?: AnalyticsMappingContext) => {
      trackProductViewed(productEventProps(payload.product, payload, analyticsContext, storeParams))
    },
    [storeParams],
  )

  const clickProduct = useCallback(
    (payload: ProductActionPayload, analyticsContext?: AnalyticsMappingContext) => {
      // every product link here hard navs to the pages router, so send the click now or we lose it
      trackProductClicked(
        productEventProps(payload.product, payload, analyticsContext, storeParams),
        { sendImmediately: true },
      )
    },
    [storeParams],
  )

  const submitBackup = useCallback(
    (commerce: ProductCommerceData, note = '') => {
      const product = { ...toMutationProduct(commerce), note }

      if (!currentOrder) {
        return updateCart(product)
      }

      const isAlreadyInOrder = currentOrder.order_lines.some(
        (line) => line.requested_product.id === product.id,
      )

      if (isAlreadyInOrder) {
        return updateOrder({
          order: currentOrder,
          products: { [product.id]: product },
        })
      }

      return addToCurrentOrder([product])
    },
    [addToCurrentOrder, currentOrder, updateCart, updateOrder],
  )

  const selectBackup = useCallback(
    ({ product: commerce }: ProductActionPayload) => {
      // submitBackup reads currentOrder through this closure and the modal keeps that
      // closure alive, so refuse to open it until the selection has resolved.
      if (isActiveOrderUnresolved) return

      openDialog(DynamicProductInstructionsModal, {
        product: toInstructionsModalProduct(commerce, {
          note: getProductNote({ id: Number(commerce.id) }),
        }),
        handleSubmit: (note = '') => submitBackup(commerce, note),
      })
    },
    [getProductNote, isActiveOrderUnresolved, openDialog, submitBackup],
  )

  return useMemo(
    () => ({ addProduct, removeProduct, viewProduct, clickProduct, selectBackup }),
    [addProduct, clickProduct, removeProduct, selectBackup, viewProduct],
  )
}
